import React, { useState } from "react";
import { Trash2, X, Plus, FileText, Upload } from "lucide-react";
import { DEAL_TYPES, ENTITY_KINDS } from "../../constants";
import { guessDocType, readFileAsAttachment } from "../../utils";
import { DocRegisterCard, type DocRegisterEntry } from "./DocRegisterCard";
import type { WorkstreamLite } from "../../types";

export interface WorkstreamCardHandlers {
  editWs: (bid: string, idx: number, patch: Partial<WorkstreamLite>) => void;
  rmWorkstream: (bid: string, wsId: string) => void;
  addEntity: (bid: string, idx: number) => void;
  rmEntity: (bid: string, idx: number, eid: string) => void;
  editEntity: (
    bid: string,
    idx: number,
    eid: string,
    patch: Record<string, unknown>,
  ) => void;
  addPerson: (bid: string, idx: number, eid: string) => void;
  rmPerson: (bid: string, idx: number, eid: string, pi: number) => void;
  editPerson: (
    bid: string,
    idx: number,
    eid: string,
    pi: number,
    col: string,
    val: string,
  ) => void;
  addMemoField: (bid: string, idx: number) => void;
  rmMemoField: (bid: string, idx: number, fi: number) => void;
  editMemoField: (
    bid: string,
    idx: number,
    fi: number,
    col: string,
    val: string,
  ) => void;
  toggleCap: (bid: string, idx: number) => void;
  setCapName: (bid: string, idx: number, name: string) => void;
  uploadDocument?: (
    bid: string,
    idx: number,
    u: { filename: string; fileData?: string },
  ) => void;
  addDocument?: (bid: string, idx: number) => void;
}

export interface WorkstreamCardProps {
  w: WorkstreamLite & { documents?: DocRegisterEntry[] };
  bid: string;
  idx: number;
  h: WorkstreamCardHandlers;
  isMobile: boolean;
}

/* One workstream card in the Library — three internal tabs: working group,
   deal memo, documents. Editing dispatches to handler callbacks on `h`. */
export function WorkstreamCard({ w, bid, idx, h, isMobile }: WorkstreamCardProps): JSX.Element {
  const [tab, setTab] = useState<string>("wg");
  const cap = w.capTable || { attached: false, name: "" };
  const rowCol: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginBottom: 10,
    paddingBottom: 10,
    borderBottom: "1px dashed var(--bd)",
  };
  const rowFlat: React.CSSProperties = {
    display: "flex",
    gap: 6,
    marginBottom: 5,
    alignItems: "center",
  };
  return (
    <div className="card" style={{ padding: isMobile ? 13 : 16 }}>
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 8,
          alignItems: isMobile ? "stretch" : "center",
          marginBottom: 12,
        }}
      >
        <input
          className="inp"
          style={{ flex: 1, fontWeight: 600 }}
          value={w.name}
          onChange={(e) => h.editWs(bid, idx, { name: e.target.value })}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <input
            className="inp mono"
            style={{
              flex: isMobile ? 1 : "none",
              width: isMobile ? "auto" : 84,
              fontSize: 12,
            }}
            value={w.code || ""}
            onChange={(e) => h.editWs(bid, idx, { code: e.target.value })}
            title="matter code"
          />
          <select
            className="sel"
            style={{ flex: isMobile ? 1 : "none", width: isMobile ? "auto" : 104 }}
            value={w.type}
            onChange={(e) => h.editWs(bid, idx, { type: e.target.value })}
          >
            <option>Matter</option>
            <option>BD</option>
          </select>
          <button
            className="icon-btn"
            title="Remove workstream"
            onClick={() => h.rmWorkstream(bid, w.id)}
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      <div className="wstabs">
        <button className={tab === "wg" ? "on" : ""} onClick={() => setTab("wg")}>
          Working group
        </button>
        <button className={tab === "memo" ? "on" : ""} onClick={() => setTab("memo")}>
          Deal memo
        </button>
        <button className={tab === "documents" ? "on" : ""} onClick={() => setTab("documents")}>
          Documents
          {(w.documents || []).length > 0 ? (
            <span className="cbadge">{(w.documents || []).length}</span>
          ) : null}
        </button>
      </div>

      {tab === "wg" && (
        <div style={{ marginTop: 12 }}>
          <div className="hint" style={{ marginBottom: 10 }}>
            Initial working group as the matter is opened — entities in the deal and the people on
            each. Start with your internal team; the simulation grows this as new participants
            appear in the thread.
          </div>
          {w.entities.length === 0 && (
            <div className="empty" style={{ padding: "14px 0" }}>
              No entities yet. Add your firm's team first.
            </div>
          )}
          {w.entities.map((en) => (
            <div key={en.id} className={"entcard" + (en.internal ? " int" : "")}>
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 8,
                  alignItems: isMobile ? "stretch" : "center",
                  marginBottom: 9,
                }}
              >
                <input
                  className="inp"
                  style={{ flex: 1, fontWeight: 600 }}
                  value={en.name}
                  onChange={(e) => h.editEntity(bid, idx, en.id, { name: e.target.value })}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <select
                    className="sel"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      width: isMobile ? "auto" : 158,
                    }}
                    value={en.kind}
                    onChange={(e) =>
                      h.editEntity(bid, idx, en.id, {
                        kind: e.target.value,
                        internal: e.target.value === "Our firm",
                      })
                    }
                  >
                    {ENTITY_KINDS.map((k) => (
                      <option key={k}>{k}</option>
                    ))}
                  </select>
                  <button
                    className="icon-btn"
                    title="Remove entity"
                    onClick={() => h.rmEntity(bid, idx, en.id)}
                  >
                    <X size={15} />
                  </button>
                </div>
              </div>
              {en.people.map((pp, pi) =>
                isMobile ? (
                  <div key={pi} style={rowCol}>
                    <input
                      className="inp"
                      placeholder="Name"
                      value={pp.name}
                      onChange={(e) => h.editPerson(bid, idx, en.id, pi, "name", e.target.value)}
                    />
                    <input
                      className="inp mono"
                      style={{ fontSize: 11 }}
                      placeholder="email@domain"
                      value={pp.email}
                      onChange={(e) => h.editPerson(bid, idx, en.id, pi, "email", e.target.value)}
                    />
                    <div style={{ display: "flex", gap: 6 }}>
                      <input
                        className="inp"
                        style={{ flex: 1 }}
                        placeholder="Role"
                        value={pp.role}
                        onChange={(e) => h.editPerson(bid, idx, en.id, pi, "role", e.target.value)}
                      />
                      <button className="icon-btn" onClick={() => h.rmPerson(bid, idx, en.id, pi)}>
                        <X size={15} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div key={pi} style={rowFlat}>
                    <input
                      className="inp"
                      style={{ flex: 1 }}
                      placeholder="Name"
                      value={pp.name}
                      onChange={(e) => h.editPerson(bid, idx, en.id, pi, "name", e.target.value)}
                    />
                    <input
                      className="inp mono"
                      style={{ flex: 1.3, fontSize: 11 }}
                      placeholder="email@domain"
                      value={pp.email}
                      onChange={(e) => h.editPerson(bid, idx, en.id, pi, "email", e.target.value)}
                    />
                    <input
                      className="inp"
                      style={{ flex: 1 }}
                      placeholder="Role"
                      value={pp.role}
                      onChange={(e) => h.editPerson(bid, idx, en.id, pi, "role", e.target.value)}
                    />
                    <button className="icon-btn" onClick={() => h.rmPerson(bid, idx, en.id, pi)}>
                      <X size={13} />
                    </button>
                  </div>
                ),
              )}
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => h.addPerson(bid, idx, en.id)}
              >
                <Plus size={12} /> Add person
              </button>
            </div>
          ))}
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginTop: 4 }}
            onClick={() => h.addEntity(bid, idx)}
          >
            <Plus size={13} /> Add entity
          </button>
        </div>
      )}

      {tab === "memo" && (
        <div style={{ marginTop: 12 }}>
          <div className="cfgf" style={{ marginBottom: 14 }}>
            <label className="up">Deal type</label>
            <select
              className="sel"
              style={{ width: isMobile ? "100%" : 300 }}
              value={w.dealType || ""}
              onChange={(e) => h.editWs(bid, idx, { dealType: e.target.value })}
            >
              <option value="">— select —</option>
              {DEAL_TYPES.map((d) => (
                <option key={d}>{d}</option>
              ))}
            </select>
            <div className="hint">From the firm's deal-type repository (~70 types).</div>
          </div>
          <div className="up" style={{ margin: "6px 0 8px" }}>
            Core terms{" "}
            <span style={{ textTransform: "none", letterSpacing: 0, color: "var(--tert)" }}>
              · high-level term-sheet elements
            </span>
          </div>
          {w.memoFields.map((f, fi) =>
            isMobile ? (
              <div key={fi} style={rowCol}>
                <input
                  className="inp"
                  placeholder="Term"
                  value={f.k}
                  onChange={(e) => h.editMemoField(bid, idx, fi, "k", e.target.value)}
                />
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    className="inp"
                    style={{ flex: 1 }}
                    placeholder="Value (may be blank at setup)"
                    value={f.v || ""}
                    onChange={(e) => h.editMemoField(bid, idx, fi, "v", e.target.value)}
                  />
                  <button className="icon-btn" onClick={() => h.rmMemoField(bid, idx, fi)}>
                    <X size={15} />
                  </button>
                </div>
              </div>
            ) : (
              <div key={fi} style={rowFlat}>
                <input
                  className="inp"
                  style={{ flex: 1 }}
                  placeholder="Term"
                  value={f.k}
                  onChange={(e) => h.editMemoField(bid, idx, fi, "k", e.target.value)}
                />
                <input
                  className="inp"
                  style={{ flex: 1.3 }}
                  placeholder="Value (may be blank at setup)"
                  value={f.v || ""}
                  onChange={(e) => h.editMemoField(bid, idx, fi, "v", e.target.value)}
                />
                <button className="icon-btn" onClick={() => h.rmMemoField(bid, idx, fi)}>
                  <X size={14} />
                </button>
              </div>
            ),
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => h.addMemoField(bid, idx)}>
            <Plus size={13} /> Add term
          </button>
          <div className="up" style={{ margin: "16px 0 8px" }}>
            Attachments
          </div>
          <div className="attachrow" style={{ flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <input
                type="checkbox"
                className="chk"
                checked={!!cap.attached}
                onChange={() => h.toggleCap(bid, idx)}
              />
              <FileText size={15} color="var(--muted)" />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 13 }}>Cap table</div>
                <div className="tert" style={{ fontSize: 11 }}>
                  Held as an attachment, not a memo field
                </div>
              </div>
            </div>
            {cap.attached && (
              <input
                className="inp mono"
                style={{
                  flex: isMobile ? "unset" : 1,
                  width: isMobile ? "100%" : "auto",
                  fontSize: 11,
                  maxWidth: isMobile ? "none" : 240,
                }}
                placeholder="filename.xlsx"
                value={cap.name || ""}
                onChange={(e) => h.setCapName(bid, idx, e.target.value)}
              />
            )}
          </div>
        </div>
      )}

      {tab === "documents" && (
        <div style={{ marginTop: 12 }}>
          <div className="hint" style={{ marginBottom: 10 }}>
            Document register — every legal document that lives in this matter, with its version
            history and which deal-memo terms it bears on. The register is populated by the spine
            as documents arrive on the thread.
          </div>
          {(!w.documents || w.documents.length === 0) && (
            <div className="empty" style={{ padding: "14px 0" }}>
              No documents registered yet. The register populates as the spine processes attached
              documents.
            </div>
          )}
          {(w.documents || []).map((d, di) => (
            <DocRegisterCard key={d.id || di} d={d} />
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 4, alignItems: "center" }}>
            {h.uploadDocument && (
              <label
                className="upbtn"
                title="Upload a .docx or .pdf into this register"
              >
                <Upload size={13} /> Upload document
                <input
                  type="file"
                  accept=".docx,.pdf,.xlsx,.doc"
                  style={{ display: "none" }}
                  onChange={async (ev) => {
                    const file = ev.target.files && ev.target.files[0];
                    ev.target.value = "";
                    if (file && h.uploadDocument) {
                      const u = await readFileAsAttachment(file);
                      h.uploadDocument(bid, idx, u);
                    }
                  }}
                />
              </label>
            )}
            {h.addDocument && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => h.addDocument && h.addDocument(bid, idx)}
              >
                <Plus size={13} /> Add document
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
